import { useTranslation } from 'react-i18next';

import ConditionTap from 'lib/Tracking/Srp/SearchFilters/ConditionTap';
import FeaturesTechnologyTap from 'lib/Tracking/Srp/SearchFilters/FeaturesTechnologyTap';
import ExpandCollapseTap from 'lib/Tracking/Srp/SearchFilters/ExpandCollapseTap';
import { useConditionFilter, useFeaturesTechnologyFilter } from 'lib/hooks/useSrpFilters';
import useEvents from 'lib/hooks/useEvents';
import { Features } from 'lib/EventSchema';

import FilterContainer from 'components/Shared/SearchFilter/FilterContainer';
import LabeledCheckBox from 'components/Shared/SearchFilter/CheckBox/LabeledCheckBox';
import PageDivider from 'components/Shared/SearchFilter/PageDivider';
import Toggle from 'components/Shared/SearchFilter/Toggle';

import { SrpConditionFilterValue, SrpConditionName } from 'types/srpFilters';
import { useContext } from 'react';
import SrpEventContext from 'contexts/SrpEventContext';

export interface ConditionFilterProps {
  expanded?: boolean;
}

export const conditionFilterDefaultState: SrpConditionFilterValue = {
  new: false,
  used: false,
  'certified pre-owned': false,
};

export enum Conditions {
  new = 'new',
  used = 'used',
  cpo = 'cpo',
  inclWarranty = 'warranty',
}

export default function ConditionFilter({ expanded }: ConditionFilterProps): JSX.Element {
  const logger = useEvents();
  const { sourcePage } = useContext(SrpEventContext);
  const { t } = useTranslation();
  const conditionFilter = useConditionFilter();
  const featuresFilter = useFeaturesTechnologyFilter();

  const handleChangeCondition = (name: SrpConditionName, isChecked: boolean): void => {
    conditionFilter.setValue({
      ...conditionFilter.value,
      [name]: isChecked,
    });
  };

  const handleChangeWarranty = (isChecked: boolean): void => {
    featuresFilter.setValue({
      ...featuresFilter.value,
      warranty: isChecked,
    });
  };

  return (
    <FilterContainer
      title={t('srp.filters.condition.header')}
      expanded={expanded}
      onClick={(isExpanding: boolean): Promise<void> =>
        logger.log(ExpandCollapseTap({ feature: Features.condition, isExpanding, sourcePage }))
      }
    >
      <div className="condition-filter-inner-container">
        <LabeledCheckBox
          label={t('srp.filters.condition.new')}
          aria-label="new-label"
          isChecked={conditionFilter.value?.new}
          onClick={(isChecked): void => {
            handleChangeCondition('new', isChecked);
            logger.log(ConditionTap(Conditions.new, sourcePage));
          }}
        />
        <LabeledCheckBox
          label={t('srp.filters.condition.used')}
          aria-label="used-label"
          isChecked={conditionFilter.value?.used}
          onClick={(isChecked): void => {
            handleChangeCondition('used', isChecked);
            logger.log(ConditionTap(Conditions.used, sourcePage));
          }}
        />
        <LabeledCheckBox
          label={t('srp.filters.condition.cpo')}
          aria-label="cpo-label"
          isChecked={conditionFilter.value?.['certified pre-owned']}
          onClick={(isChecked): void => {
            handleChangeCondition('certified pre-owned', isChecked);
            logger.log(ConditionTap(Conditions.cpo, sourcePage));
          }}
        />
        <div className="condition-divider-container">
          <PageDivider />
        </div>
        <Toggle
          label={t('srp.filters.condition.inclWarranty')}
          id="incl-warranty"
          isToggled={featuresFilter.value?.warranty}
          onClick={(isChecked): void => {
            handleChangeWarranty(isChecked);
            logger.log(FeaturesTechnologyTap('warranty', sourcePage));
          }}
        />
      </div>
      <style jsx>{`
        .condition-filter-inner-container {
          padding-bottom: 8px;
        }
        .condition-divider-container {
          margin: 8px 0;
        }
      `}</style>
    </FilterContainer>
  );
}
